// @ts-check
/**
 * @module core/xref
 *
 * Automatically adds external references.
 *
 * Searches for the terms which do not have a local definition at xref API and
 * for each query, adds `data-cite` attributes to respective elements.
 * `core/data-cite` later converts these data-cite attributes to actual links.
 * https://github.com/speced/respec/issues/1662
 */
/**
 * @typedef {import('core/xref').RequestEntry} RequestEntry
 * @typedef {import('core/xref').Response} Response
 * @typedef {import('core/xref').SearchResultEntry} SearchResultEntry
 * @typedef {Map<string, { elems: HTMLElement[], results: SearchResultEntry[], query: RequestEntry }>} ErrorCollection
 * @typedef {{ ambiguous: ErrorCollection, notFound: ErrorCollection }} Errors
 */
import {
  POSSESSIVE_SUFFIX,
  createResourceHint,
  docLink,
  joinAnd,
  joinOr,
  nonNormativeSelector,
  norm as normalize,
  showError,
} from "./utils.js";
import { cacheXrefData, resolveXrefCache } from "./xref-db.js";
import { possibleExternalLinks } from "./link-to-dfn.js";
import { sub } from "./pubsubhub.js";

export const name = "core/xref";

const profiles = {
  "web-platform": ["HTML", "INFRA", "URL", "WEBIDL", "DOM", "FETCH"],
};

export const API_URL = "https://respec.org/xref/";

/** @type {{ term: string; spec: string; element: HTMLElement }[]} */
export const informativeRefsInNormative = [];

if (
  !document.querySelector("link[rel='preconnect'][href='https://respec.org']")
) {
  const link = createResourceHint({
    hint: "preconnect",
    href: "https://respec.org",
  });
  document.head.appendChild(link);
}

/**
 * @param {Conf} conf respecConfig
 */
export async function run(conf) {
  if (!conf.xref) {
    return;
  }

  const xref = normalizeConfig(conf.xref);
  if (xref.specs) {
    const bodyCite = document.body.dataset.cite
      ? document.body.dataset.cite.split(/\s+/)
      : [];
    document.body.dataset.cite = bodyCite.concat(xref.specs).join(" ");
  }

  const elems = possibleExternalLinks.concat(findExplicitExternalLinks());
  if (!elems.length) return;

  /** @type {RequestEntry[]} */
  const queryKeys = [];
  for (const elem of elems) {
    const entry = getRequestEntry(elem);
    entry.id = await objectHash(entry);
    queryKeys.push(entry);
  }

  const data = await getData(queryKeys, xref.url);
  addDataCiteToTerms(elems, queryKeys, data, conf);

  sub("beforesave", cleanup);
}

/**
 * Find additional references that need to be looked up externally.
 * Examples: a[data-cite="spec"], dfn[data-cite="spec"], dfn.externalDFN
 */
function findExplicitExternalLinks() {
  /** @type {NodeListOf<HTMLElement>} */
  const links = document.querySelectorAll(
    ":is(a,dfn)[data-cite]:not([data-cite=''],[data-cite*='#'])"
  );
  /** @type {NodeListOf<HTMLElement>} */
  const externalDFNs = document.querySelectorAll("dfn.externalDFN");
  return [...links]
    .filter(el => {
      // ignore empties
      if (el.textContent.trim() === "") return false;
      /** @type {HTMLElement | null} */
      const closest = el.closest("[data-cite]");
      return !closest || closest.dataset.cite !== "";
    })
    .concat(...externalDFNs);
}

/**
 * @param {boolean | string | string[] | { profile?: string; url?: string; specs?: string[] }} xref
 * converts conf.xref to object with url and spec properties
 */
function normalizeConfig(xref) {
  const defaults = {
    url: new URL("search/", API_URL).href,
    specs: null,
  };

  const config = Object.assign({}, defaults);

  const type = Array.isArray(xref) ? "array" : typeof xref;
  switch (type) {
    case "boolean":
      // using defaults already, as above
      break;
    case "string": {
      const xrefStr = /** @type {string} */ (xref);
      if (xrefStr.toLowerCase() in profiles) {
        Object.assign(config, {
          specs: /** @type {Record<string, string[]>} */ (profiles)[
            xrefStr.toLowerCase()
          ],
        });
      } else {
        invalidProfileError(xrefStr);
      }
      break;
    }
    case "array":
      Object.assign(config, { specs: xref });
      break;
    case "object": {
      const xrefObj =
        /** @type {{ profile?: string; url?: string; specs?: string[] }} */ (
          xref
        );
      Object.assign(config, xrefObj);
      if (xrefObj.profile) {
        const profile = xrefObj.profile.toLowerCase();
        if (profile in profiles) {
          const specs = (xrefObj.specs ?? []).concat(
            /** @type {Record<string, string[]>} */ (profiles)[profile]
          );
          Object.assign(config, { specs });
        } else {
          invalidProfileError(xrefObj.profile);
        }
      }
      break;
    }
    default: {
      const msg = `Invalid value for \`xref\` configuration option. Received: "${xref}".`;
      const hint = docLink`Expected: \`true\`, a profile name (e.g. \`"web-platform"\`), an array of spec shortnames (e.g. \`["FETCH", "DOM"]\`), or an object with \`url\`, \`specs\`, or \`profile\` properties. See ${"[xref]"}.`;
      showError(msg, name, { hint });
    }
  }
  return config;

  /** @param {string} profile */
  function invalidProfileError(profile) {
    const supportedProfiles = joinOr(Object.keys(profiles), s => `"${s}"`);
    const msg =
      `Invalid profile "${profile}" in \`respecConfig.xref\`. ` +
      `Please use one of the supported profiles: ${supportedProfiles}.`;
    showError(msg, name);
  }
}

/**
 * get xref API request entry (term and context) for given xref element
 * @param {HTMLElement} elem
 */
function getRequestEntry(elem) {
  const isIDL = "xrefType" in elem.dataset;

  let term = getTermFromElement(elem, { isIDL });
  if (!isIDL) term = term.toLowerCase();

  const specs = getSpecContext(elem);
  const types = getTypeContext(elem, isIDL);
  const forContext = getForContext(elem, isIDL);

  return {
    // Add an empty `id` to ensure the shape of object returned stays same when
    // actual `id` is added later (minor perf optimization, also makes
    // TypeScript happy).
    id: "",
    term,
    types,
    ...(specs.length && { specs }),
    ...(typeof forContext === "string" && { for: forContext }),
  };
}

/** @param {HTMLElement} elem */
/**
 * @param {HTMLElement} elem
 * @param {{ isIDL?: boolean }} [options]
 */
export function getTermFromElement(elem, { isIDL = false } = {}) {
  const { lt: linkingText } = elem.dataset;
  let term = linkingText ? linkingText.split("|", 1)[0] : elem.textContent;
  term = normalize(term);
  if (term === "the-empty-string") return "";
  if (!isIDL && !linkingText) term = term.replace(POSSESSIVE_SUFFIX, "");
  return term;
}

/**
 * @param {string} spec
 */
function stripVersionSuffix(spec) {
  return spec.replace(/-\d+$/, "");
}

/**
 * Get spec context as a fallback chain, where each level (sub-array) represents
 * decreasing priority.
 *
 * For versioned spec names (e.g. "service-workers-1"), the unversioned form
 * ("service-workers") is added as a **separate lower-priority level** rather
 * than at the same level.  Keeping them at different levels avoids returning
 * two results for the same term when the xref data indexes the definition under
 * both the versioned and unversioned shortname, which would otherwise trigger a
 * spurious "ambiguous dfn" error.
 *
 * @param {HTMLElement} elem
 */
function getSpecContext(elem) {
  /** @type {string[][]} */
  const specs = [];

  /** @type {HTMLElement | null} */
  let dataciteElem = elem.closest("[data-cite]");

  // Traverse up towards the root element, adding levels of lower priority specs
  while (dataciteElem) {
    const cite = (dataciteElem.dataset.cite ?? "")
      .toLowerCase()
      .replace(/[!?]/g, "");
    const cites = cite.split(/\s+/).filter(s => s);
    if (cites.length) {
      specs.push(cites);
      // Add the unversioned forms (e.g. "service-workers" for "service-workers-1")
      // at a lower priority so the server only falls back to them when the
      // versioned name yields no result.  This prevents ambiguous-dfn errors if
      // a term is indexed under both shortname variants.
      const unversioned = getUnversionedFallbacks(cites);
      if (unversioned.length) {
        specs.push(unversioned);
      }
    }
    if (dataciteElem === elem) break;
    dataciteElem = dataciteElem.parentElement?.closest("[data-cite]") ?? null;
  }

  // If element itself contains data-cite, we don't take inline context into
  // account. The inline bibref context has lowest priority, if available.
  if (dataciteElem !== elem) {
    const closestSection = elem.closest("section");
    /** @type {Iterable<HTMLElement>} */
    const bibrefs = closestSection
      ? closestSection.querySelectorAll("a.bibref")
      : [];
    const inlineRefList = [
      ...new Set([...bibrefs].map(el => el.textContent.toLowerCase())),
    ];
    if (inlineRefList.length) {
      specs.push(inlineRefList);
      const unversioned = getUnversionedFallbacks(inlineRefList);
      if (unversioned.length) {
        specs.push(unversioned);
      }
    }
  }

  const uniqueSpecContext = dedupeSpecContext(specs);
  return uniqueSpecContext;
}

/**
 * Returns specs whose unversioned form differs from the original,
 * e.g., ["css-grid-2"] → ["css-grid"] (excluding "css-grid-2" itself).
 * @param {string[]} specs
 */
function getUnversionedFallbacks(specs) {
  return [...new Set(specs.map(stripVersionSuffix)).difference(new Set(specs))];
}

/**
 * If we already have a spec in a higher priority level (closer to element) of
 * fallback chain, skip it from low priority levels, to prevent duplication.
 * @param {string[][]} specs
 * */
function dedupeSpecContext(specs) {
  /** @type {string[][]} */
  const unique = [];
  /** @type {Set<string>} tracks all specs seen in higher-priority levels */
  const seen = new Set();
  for (const level of specs) {
    const uniqueSpecs = [...new Set(level).values().filter(s => !seen.has(s))];
    uniqueSpecs.forEach(s => seen.add(s));
    unique.push(uniqueSpecs.sort());
  }
  return unique;
}

/**
 * @param {HTMLElement} elem
 * @param {boolean} isIDL
 */
function getForContext(elem, isIDL) {
  if (elem.dataset.xrefFor) {
    return normalize(elem.dataset.xrefFor);
  }

  if (isIDL) {
    /** @type {HTMLElement | null} */
    const dataXrefForElem = elem.closest("[data-xref-for]");
    if (dataXrefForElem) {
      return normalize(dataXrefForElem.dataset.xrefFor ?? "");
    }
  }

  return null;
}

/**
 * @param {HTMLElement} elem
 * @param {boolean} isIDL
 */
function getTypeContext(elem, isIDL) {
  if (isIDL) {
    if (elem.dataset.xrefType) {
      return elem.dataset.xrefType.split("|");
    }
    return ["_IDL_"];
  }

  return ["_CONCEPT_"];
}

/**
 * @param {RequestEntry[]} queryKeys
 * @param {string} apiUrl
 * @returns {Promise<Map<string, SearchResultEntry[]>>}
 */
async function getData(queryKeys, apiUrl) {
  const uniqueIds = new Set();
  const uniqueQueryKeys = queryKeys.filter(key => {
    return uniqueIds.has(key.id) ? false : uniqueIds.add(key.id) && true;
  });

  const resultsFromCache = await resolveXrefCache(uniqueQueryKeys);

  const termsToLook = uniqueQueryKeys.filter(
    key => !resultsFromCache.get(key.id)
  );
  const fetchedResults = await fetchFromNetwork(termsToLook, apiUrl);
  if (fetchedResults.size) {
    // add data to cache
    await cacheXrefData(uniqueQueryKeys, fetchedResults);
  }

  return new Map([...resultsFromCache, ...fetchedResults]);
}

/**
 * @param {RequestEntry[]} queries
 * @param {string} url
 * @returns {Promise<Map<string, SearchResultEntry[]>>}
 */
async function fetchFromNetwork(queries, url) {
  if (!queries.length) return new Map();

  const options = {
    method: "POST",
    body: JSON.stringify({ queries }),
    headers: {
      "Content-Type": "application/json",
    },
  };
  const response = await fetch(url, options);
  /** @type {{ results: { id: string; result: SearchResultEntry[] }[] } }} */
  const json = await response.json();
  return new Map(json.results.map(({ id, result }) => [id, result]));
}

/**
 * Figures out from the tree structure if the reference is
 * normative (true) or informative (false).
 * @param {HTMLElement} elem
 */
function isNormative(elem) {
  const closestNormative = elem.closest(".normative");
  const closestInform = elem.closest(nonNormativeSelector);
  if (!closestInform || elem === closestNormative) {
    return true;
  }
  return (
    closestNormative &&
    closestInform &&
    closestInform.contains(closestNormative)
  );
}

/**
 * adds data-cite attributes to elems for each term for which results are found.
 * adds citations to references section.
 * collects and shows linking errors if any.
 * @param {HTMLElement[]} elems
 * @param {RequestEntry[]} queryKeys
 * @param {Map<string, SearchResultEntry[]>} data
 * @param {Conf} conf
 */
function addDataCiteToTerms(elems, queryKeys, data, conf) {
  /** @type {Errors} */
  const errors = { ambiguous: new Map(), notFound: new Map() };

  for (let i = 0, l = elems.length; i < l; i++) {
    if (elems[i].closest("[data-no-xref]")) continue;

    const elem = elems[i];
    const query = queryKeys[i];

    const { id } = query;
    const results = data.get(id) ?? [];
    if (results.length === 1) {
      addDataCite(elem, query, results[0], conf);
    } else {
      const collector = errors[results.length === 0 ? "notFound" : "ambiguous"];
      if (!collector.has(id)) {
        collector.set(id, { elems: [], results, query });
      }
      collector.get(id)?.elems.push(elem);
    }
  }

  showErrors(errors);
}

/**
 * @param {HTMLElement} elem
 * @param {RequestEntry} query
 * @param {SearchResultEntry} result
 * @param {Conf} conf
 */
function addDataCite(elem, query, result, conf) {
  const { term, specs = [] } = query;
  const { uri, shortname, spec, normative, type, for: forContext } = result;
  // if authored spec context had `result.spec`, use it instead of shortname
  const cite = specs.flat().includes(spec) ? spec : shortname;
  // we use this "partial" URL to resolve parts of urls...
  // but sometimes we get lucky and we get an absolute URL from xref
  // which we can then use in other places (e.g., data-cite.js)
  const url = new URL(uri, "https://partial");
  let { pathname: citePath } = url;
  // final resolution will be against the URL of the spec, which may end with
  // a filename. That filename must be preserved if there's no specific path.
  if (citePath === "/") citePath = "";
  const citeFrag = url.hash.slice(1);
  const dataset = /** @type {Record<string, string>} */ ({
    cite,
    citePath,
    citeFrag,
    linkType: type,
  });
  if (forContext) dataset.linkFor = forContext[0];
  if (url.origin && url.origin !== "https://partial") {
    dataset.citeHref = url.href;
  }
  Object.assign(elem.dataset, dataset);

  addToReferences(elem, cite, normative, term, conf);
}

/**
 * add specs for citation (references section)
 * @param {HTMLElement} elem
 * @param {string} cite
 * @param {boolean} normative
 * @param {string} term
 * @param {Conf} conf
 */
function addToReferences(elem, cite, normative, term, conf) {
  const isNormRef = isNormative(elem);
  if (!isNormRef) {
    // Only add it if not already normative...
    if (!conf.normativeReferences.has(cite)) {
      conf.informativeReferences.add(cite);
    }
    return;
  }
  if (normative) {
    // If it was originally informative, we move the existing
    // key to be normative.
    const existingKey = conf.informativeReferences.has(cite)
      ? (conf.informativeReferences.getCanonicalKey(cite) ?? cite)
      : cite;
    conf.normativeReferences.add(existingKey);
    conf.informativeReferences.delete(existingKey);
    return;
  }

  // This is used by the informative-dfn linter
  informativeRefsInNormative.push({ term, spec: cite, element: elem });
}

/** @param {Errors} errors */
function showErrors({ ambiguous, notFound }) {
  /**
   * @param {string} term
   * @param {RequestEntry} query
   * @param {string[]} specs
   */
  const getPrefilledFormURL = (term, query, specs = []) => {
    const url = new URL(API_URL);
    url.searchParams.set("term", term);
    if (query.for) url.searchParams.set("for", query.for);
    url.searchParams.set("types", query.types.join(","));
    if (specs.length) url.searchParams.set("specs", specs.join(","));
    return url.href;
  };

  /**
   * @param {string} howToCiteURL
   * @param {string} originalTerm
   */
  const howToFix = (howToCiteURL, originalTerm) => {
    return docLink`[See search matches for "${originalTerm}"](${howToCiteURL}) or ${"[Learn about this error|#error-term-not-found]"}.`;
  };

  for (const { query, elems } of notFound.values()) {
    const specs = query.specs ? [...new Set(query.specs.flat())].sort() : [];
    const originalTerm = getTermFromElement(elems[0]);
    const formUrl = getPrefilledFormURL(originalTerm, query);
    const specsString = joinAnd(specs, s => `**[${s}]**`);
    let hint = howToFix(formUrl, originalTerm);
    /** @type {HTMLElement | null} */
    const closestCite = elems[0].parentElement?.closest("[data-cite]") ?? null;
    const citeAttr = closestCite?.dataset.cite?.replace(/`/g, "") ?? "";
    if (closestCite && closestCite !== document.body && citeAttr) {
      hint += ` A parent element has \`data-cite="${citeAttr}"\` — check that the spec shortname is correct.`;
    }
    const forParent = query.for ? `, for **"${query.for}"**, ` : "";
    const msg = `Couldn't find "**${originalTerm}**"${forParent} in this document or other cited documents: ${specsString}.`;
    const title = "No matching definition found.";
    showError(msg, name, { title, elements: elems, hint });
  }

  for (const { query, elems, results } of ambiguous.values()) {
    const specs = [...new Set(results.map(entry => entry.shortname))].sort();
    const specsString = joinAnd(specs, s => `**[${s}]**`);
    const originalTerm = getTermFromElement(elems[0]);
    const formUrl = getPrefilledFormURL(originalTerm, query, specs);
    const forParent = query.for ? `, for **"${query.for}"**, ` : "";
    const moreInfo = howToFix(formUrl, originalTerm);
    const hint =
      docLink`To fix, use the ${"[data-cite]"} attribute to pick the one you mean from the appropriate specification.` +
      String.raw` ${moreInfo}`;
    const msg = `The term "**${originalTerm}**"${forParent} is ambiguous because it's defined in ${specsString}.`;
    const title = "Definition is ambiguous.";
    showError(msg, name, { title, elements: elems, hint });
  }
}

/** @param {RequestEntry} obj */
function objectHash(obj) {
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  const buffer = new TextEncoder().encode(str);
  return crypto.subtle.digest("SHA-1", buffer).then(bufferToHexString);
}

/** @param {ArrayBuffer} buffer */
function bufferToHexString(buffer) {
  const byteArray = new Uint8Array(buffer);
  return (
    byteArray.toHex?.() ??
    [...byteArray].map(v => v.toString(16).padStart(2, "0")).join("")
  );
}

/** @param {Document} doc */
function cleanup(doc) {
  /** @type {NodeListOf<HTMLAnchorElement>} */
  const elems = doc.querySelectorAll(
    "a[data-xref-for], a[data-xref-type], a[data-link-for]"
  );
  const attrToRemove = ["data-xref-for", "data-xref-type", "data-link-for"];
  elems.forEach(el => {
    attrToRemove.forEach(attr => el.removeAttribute(attr));
  });
}
